import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { atomWithInfiniteQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import { settingsLogService, type SettingsLogFilter } from '@/services/settings-log.service';

const pagesQueryAtom = (filter: SettingsLogFilter) =>
	atomWithInfiniteQuery((get) => ({
		queryKey: ['settings-logs', buScopeKey(get), filter],
		queryFn: ({ pageParam }: { pageParam: string | undefined }) => settingsLogService.fetchPage(filter, pageParam, buScopeParams(get)),
		initialPageParam: undefined as string | undefined,
		// `nextBefore` is an opaque position (the last entry's time and id) across both audit stores.
		getNextPageParam: (lastPage: { hasMore: boolean; nextBefore: string | null }) =>
			lastPage.hasMore ? (lastPage.nextBefore ?? undefined) : undefined,
		enabled: get(isAuthenticatedAtom)
	}));

/** The selected brand's settings changes, newest first, a page at a time. `filter` must be memoised. */
export function useSettingsLog(filter: SettingsLogFilter) {
	const queryAtom = useMemo(() => pagesQueryAtom(filter), [filter]);
	const { data, isLoading, isError, isFetchNextPageError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useAtomValue(queryAtom);
	const entries = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);
	const loadMore = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);
	return {
		entries,
		isLoading,
		isError,
		loadMoreFailed: isFetchNextPageError,
		refetch,
		hasMore: Boolean(hasNextPage),
		loadingMore: isFetchingNextPage,
		loadMore
	};
}
